import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

function redirectAfterAuth(user, navigate) {
  if (user?.role === 'super_admin') {
    navigate('/admin');
  } else if (user?.role === 'sales_rep' || user?.role === 'dealer_user') {
    navigate('/my-home');
  } else {
    navigate('/dashboard');
  }
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      redirectAfterAuth(user, navigate);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail?.trim()) {
      toast.error('Enter your email');
      return;
    }
    setForgotSending(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.trim() });
      toast.success('If an account exists for that email, you will receive reset instructions.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0a0f1e 0%, #0d1b2a 50%, #0a1628 100%)',
      }}
    >
      {/* Animated background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute rounded-full opacity-15 blur-3xl"
          style={{
            width: '700px',
            height: '700px',
            top: '-250px',
            left: '-250px',
            background: 'radial-gradient(circle, #00c6c6, #0891b2, transparent)',
            animation: 'float1 9s ease-in-out infinite',
          }}
        />
        <div
          className="absolute rounded-full opacity-15 blur-3xl"
          style={{
            width: '600px',
            height: '600px',
            bottom: '-200px',
            right: '-200px',
            background: 'radial-gradient(circle, #3b82f6, #1d4ed8, transparent)',
            animation: 'float2 11s ease-in-out infinite',
          }}
        />
        <div
          className="absolute rounded-full opacity-10 blur-3xl"
          style={{
            width: '400px',
            height: '400px',
            top: '10%',
            right: '-100px',
            background: 'radial-gradient(circle, #06b6d4, transparent)',
            animation: 'float3 13s ease-in-out infinite',
          }}
        />
        <div
          className="absolute rounded-full blur-3xl"
          style={{
            width: '300px',
            height: '300px',
            bottom: '10%',
            left: '-50px',
            opacity: 0.08,
            background: 'radial-gradient(circle, #f5a623, transparent)',
            animation: 'float4 15s ease-in-out infinite',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            opacity: 0.03,
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }}
        />
      </div>

      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, -40px) scale(1.05); }
          66% { transform: translate(-20px, 25px) scale(0.95); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-50px, 25px) scale(1.08); }
          66% { transform: translate(25px, -35px) scale(0.93); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-30px, 40px); }
        }
        @keyframes float4 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(20px, -20px) scale(1.1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes logoGlow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(0, 198, 198, 0.3)); }
          50% { filter: drop-shadow(0 0 35px rgba(0, 198, 198, 0.5)); }
        }
      `}</style>

      {/* Login card */}
      <div
        className="relative z-10 w-full max-w-md mx-4"
        style={{ animation: 'fadeUp 0.7s ease-out' }}
      >
        <div
          className="rounded-2xl p-8"
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(0, 198, 198, 0.2)',
            boxShadow:
              '0 25px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/logo.svg"
              alt="LotMate"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              className="w-auto max-w-[min(100%,380px)]"
              style={{
                height: 'clamp(9.5rem, 30vw, 13rem)',
                width: 'auto',
                objectFit: 'contain',
                userSelect: 'none',
                WebkitUserDrag: 'none',
                pointerEvents: 'none',
                animation: 'logoGlow 3s ease-in-out infinite',
              }}
            />
          </div>

          {/* Welcome */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white mb-1">Welcome back</h1>
            <p style={{ color: 'rgba(255,255,255,0.45)' }} className="text-sm">
              Sign in to your account to continue
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-4 p-3 rounded-xl text-sm"
              style={{
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                color: '#fca5a5',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'rgba(255,255,255,0.6)' }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dealership.com"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                data-testid="login-email-input"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'white',
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid rgba(0, 198, 198, 0.6)';
                  e.target.style.background = 'rgba(0, 198, 198, 0.08)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 198, 198, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid rgba(255,255,255,0.1)';
                  e.target.style.background = 'rgba(255,255,255,0.06)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Password */}
            <div className="mb-6">
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: 'rgba(255,255,255,0.6)' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 pr-16"
                  data-testid="login-password-input"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'white',
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid rgba(0, 198, 198, 0.6)';
                    e.target.style.background = 'rgba(0, 198, 198, 0.08)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(0, 198, 198, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid rgba(255,255,255,0.1)';
                    e.target.style.background = 'rgba(255,255,255,0.06)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium transition-colors duration-200"
                  style={{ color: 'rgba(0, 198, 198, 0.7)' }}
                  onMouseEnter={(e) => (e.target.style.color = 'rgba(0, 198, 198, 1)')}
                  onMouseLeave={(e) => (e.target.style.color = 'rgba(0, 198, 198, 0.7)')}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {/* Sign in button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
              style={{
                background: loading
                  ? 'rgba(0, 198, 198, 0.3)'
                  : 'linear-gradient(135deg, #00c6c6 0%, #0891b2 50%, #3b82f6 100%)',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(0, 198, 198, 0.35)',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 198, 198, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = loading
                  ? 'none'
                  : '0 4px 20px rgba(0, 198, 198, 0.35)';
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full border-2 animate-spin"
                    style={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                    }}
                  />
                  Signing in...
                </div>
              ) : (
                'Sign In \u2192'
              )}
            </button>
          </form>

          {/* Forgot password */}
          <div className="flex items-center justify-center mt-5">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-xs transition-colors duration-200"
              data-testid="forgot-password-link"
              style={{ color: 'rgba(255,255,255,0.35)' }}
              onMouseEnter={(e) => (e.target.style.color = 'rgba(0, 198, 198, 0.8)')}
              onMouseLeave={(e) => (e.target.style.color = 'rgba(255,255,255,0.35)')}
            >
              Forgot password?
            </button>
          </div>
        </div>

        <p
          className="text-center text-xs mt-6"
          style={{ color: 'rgba(255,255,255,0.2)' }}
        >
          &copy; 2026 LotMate
        </p>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Enter your email and we&apos;ll send you a reset link if an account exists.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="forgot-email-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForgotPassword(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={forgotSending} data-testid="forgot-submit-btn">
                {forgotSending ? 'Sending\u2026' : 'Send reset link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
